import { forwardRef, useState } from "react";
import { StepRef, StepProps } from "../types";
import { Typography, Box, Button, Alert } from "@mui/material";
import useIsMobile from "components/hooks/useIsMobile";
import StaticStep from "../components/StaticStep";
import PasswordInput from "components/common/PasswordInput";
import { validatePassword } from "common/utils";
import AuthService from "services/auth";
import TextField from "components/common/TextField";
import Checkbox from "components/common/Checkbox";
import { TOS_VERSION } from "components/context/CheckTerms";
import { getBrowserTimezone } from "common/timezoneUtils";
import LinkRouter from "components/navigation/LinkRouter";

interface FormData {
  email: string;
  firstName: string;
  lastName: string;
  password: string;
}

interface FormErrors {
  [key: string]: string;
}

export default forwardRef<StepRef, StepProps>(
  ({ onNext, disabled, signupState }, _ref) => {
    const isMobile = useIsMobile();
    const [formData, setFormData] = useState<FormData>({
      email: signupState?.userInfo?.email || "",
      firstName: signupState?.userInfo?.firstName || "",
      lastName: signupState?.userInfo?.lastName || "",
      password: "",
    });
    const [errors, setErrors] = useState<FormErrors>({});
    const [hasAcceptedToS, setHasAcceptedToS] = useState(false);

    const { request: createPasswordRequest, loading: createPasswordLoading } =
      AuthService.useCreatePassword();

    const validateForm = (): FormErrors => {
      const newErrors: FormErrors = {};

      if (!formData.email.trim()) {
        newErrors.email = "Email is required";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = "Invalid email address";
      }

      if (!formData.firstName.trim()) {
        newErrors.firstName = "First name is required";
      }

      if (!formData.lastName.trim()) {
        newErrors.lastName = "Last name is required";
      }

      const passwordErrors = validatePassword(formData.password);
      Object.assign(newErrors, passwordErrors);

      return newErrors;
    };

    const handleContinue = async () => {
      const validationErrors = validateForm();

      if (!hasAcceptedToS) {
        validationErrors.termsOfService =
          "You must accept the terms of service";
      }

      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        return;
      }

      try {
        const timezone = getBrowserTimezone();
        const response = await onNext?.({
          userInfo: {
            email: formData.email,
            firstName: formData.firstName,
            lastName: formData.lastName,
            address: signupState?.userInfo?.address || "",
            timezone: timezone,
          },
          termsOfServiceVersion: TOS_VERSION,
        });
        if (response && "token" in response && response.token) {
          const loginResponse = await AuthService.signupTokenLogin(
            response.token,
          );
          if (!loginResponse?.currentUser?.isPasswordSet) {
            await createPasswordRequest({ password: formData.password });
          }
        }
      } catch (error) {
        if (
          error instanceof Error &&
          error.message.includes("already in use")
        ) {
          setErrors({ accountExists: formData.email });
        } else {
          setErrors({ general: "Failed to create account. Please try again." });
        }
      }
    };

    const createChangeHandler = (field: keyof FormData, value: string) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: "" }));
      }
    };

    return (
      <StaticStep
        title="Create your profile and start adding tasks"
        img="/assets/images/nux/signup-confirmation.png"
        mobileImg="/assets/images/nux/signup-confirmation-mobile.png"
        fullImageHeight={true}
        mobileImgSx={{
          borderRadius: "0",
          margin: "-16px auto 8px auto",
          marginBottom: "8px",
          maxHeight: "30vh",
          width: "auto",
          borderBottom: "1px solid #D4D4D4",
        }}
        bodyContent={
          <>
            <Typography
              variant="h4"
              sx={{ marginBottom: "12px", fontWeight: "500" }}
            >
              No payment required until you're ready to be matched with an
              Advisor.
            </Typography>

            {errors.general && (
              <Alert severity="error" sx={{ marginBottom: "16px" }}>
                {errors.general}
              </Alert>
            )}

            {errors.accountExists && (
              <Alert
                severity="info"
                sx={{
                  marginBottom: "16px",
                  borderRadius: "12px",
                  border: "1px solid #B8D4E3",
                  background: "#F8FBFD",
                }}
              >
                <Typography variant="body" sx={{ display: "inline" }}>
                  Looks like you already have an account with us!{" "}
                  <LinkRouter
                    inline
                    to={`/member-login/link?email=${encodeURIComponent(
                      errors.accountExists,
                    )}`}
                  >
                    <Typography
                      variant="body"
                      sx={{
                        color: "primary.main",
                        cursor: "pointer",
                        display: "inline",
                        fontWeight: 600,
                      }}
                    >
                      Sign in
                    </Typography>
                  </LinkRouter>{" "}
                  to pick up where you left off.
                </Typography>
              </Alert>
            )}

            <Box sx={{ display: "flex", gap: "12px" }}>
              <TextField
                sx={{ flex: 1 }}
                label="First name"
                type="text"
                variant="outlined"
                name="firstName"
                autoComplete="given-name"
                value={formData.firstName}
                error={!!errors.firstName}
                helperText={errors.firstName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  createChangeHandler("firstName", e.target.value)
                }
              />
              <TextField
                sx={{ flex: 1 }}
                label="Last name"
                type="text"
                variant="outlined"
                name="lastName"
                autoComplete="family-name"
                value={formData.lastName}
                error={!!errors.lastName}
                helperText={errors.lastName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  createChangeHandler("lastName", e.target.value)
                }
              />
            </Box>

            <Box sx={{}}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                variant="outlined"
                name="email"
                autoComplete="email"
                value={formData.email}
                error={!!errors.email}
                helperText={errors.email}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  createChangeHandler("email", e.target.value)
                }
              />
            </Box>

            <Box sx={{}}>
              <PasswordInput
                password={formData.password}
                onChange={(password: string) =>
                  createChangeHandler("password", password)
                }
                sx={{ width: "100%" }}
              />
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
              }}
            >
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                }}
              >
                <Checkbox
                  checked={hasAcceptedToS}
                  onChange={() => {
                    setHasAcceptedToS(!hasAcceptedToS);
                  }}
                />
                <Typography variant="body">
                  I agree to the{" "}
                  <a
                    href="https://www.findfaye.com/client-terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: "#198282",
                      textDecoration: "none",
                    }}
                  >
                    Terms of Service
                  </a>
                </Typography>
              </label>
              {errors.termsOfService && (
                <Typography
                  variant="bodySmall"
                  sx={{
                    color: "#C14743",
                    marginTop: "4px",
                    marginLeft: "32px",
                  }}
                >
                  {errors.termsOfService}
                </Typography>
              )}
            </Box>
          </>
        }
        actions={
          <>
            <Button
              key={0}
              variant="contained"
              color="primary"
              size={isMobile ? "medium" : "large"}
              disabled={disabled || createPasswordLoading || !hasAcceptedToS}
              onClick={handleContinue}
              sx={{ width: "100%", marginBottom: "16px" }}
            >
              Continue
            </Button>
          </>
        }
      />
    );
  },
);
